import { create } from 'zustand';
import { InfiniteScrollStatus } from '../types/enums/InfiniteScrollStatus';
import { NetworkException } from '../utils/apiException';

interface InfiniteScrollStore<T> {
  data: T[];
  extra: any;
  pageKey: string | null;
  loading: boolean;
  error: any;
  hasMore: boolean;
  status: InfiniteScrollStatus;
}

interface InfiniteScrollActions<T> {
  setData: (data: T[], extra?: any) => void;
  setPageKey: (pageKey: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: any) => void;
  setHasMore: (hasMore: boolean) => void;
  refresh: () => void;
}

const createInfiniteScrollStore = <T>() => {
  return create<InfiniteScrollStore<T> & InfiniteScrollActions<T>>(
    (set, get) => ({
      data: [],
      extra: null,
      pageKey: null,
      loading: false,
      error: null,
      hasMore: true,
      status: InfiniteScrollStatus.InitialLoading,
      setData: (data, extra = null) => {
        set((state) => ({
          data: [...state.data, ...data],
          extra: extra || state.extra,
        }));
        set({ status: calculateStatus(get()) });
      },
      setPageKey: (pageKey) => set({ pageKey }),
      setLoading: (loading) => {
        set({ loading });
        set({ status: calculateStatus(get()) });
      },
      setError: (error) => {
        set({ error });
        set({ status: calculateStatus(get()) });
      },
      setHasMore: (hasMore) => {
        set({ hasMore });
        set({ status: calculateStatus(get()) });
      },
      refresh: () =>
        set({
          data: [],
          extra: null,
          pageKey: null,
          error: null,
          hasMore: true,
          loading: false,
          status: InfiniteScrollStatus.InitialLoading,
        }),
    })
  );
};

const calculateStatus = <T>({
  data,
  loading,
  error,
  hasMore,
}: InfiniteScrollStore<T>): InfiniteScrollStatus => {
  if (loading && data.length === 0) return InfiniteScrollStatus.InitialLoading;
  if (!loading && !error && data.length === 0)
    return InfiniteScrollStatus.NoItems;
  if (!hasMore && data.length > 0) return InfiniteScrollStatus.NoMoreItems;
  if (error && data.length === 0 && error instanceof NetworkException)
    return InfiniteScrollStatus.NetworkErrorWithNoData;
  if (error && data.length === 0 && !(error instanceof NetworkException))
    return InfiniteScrollStatus.ServerErrorWithNoData;
  if (error && data.length > 0 && error instanceof NetworkException)
    return InfiniteScrollStatus.NetworkErrorWithMoreData;
  if (error && data.length > 0 && !(error instanceof NetworkException))
    return InfiniteScrollStatus.ServerErrorWithMoreData;
  if (data.length > 0 && hasMore && !error)
    return InfiniteScrollStatus.ReadyToLoadMore;
  return InfiniteScrollStatus.InitialLoading;
};

export default createInfiniteScrollStore;
